import { useEffect, useRef, useCallback, useState } from 'react';
import { fetchUserPreferences, updateUserPreferences } from '@/features/edgemail-client/services/MailClientAPI';
import { useUserPreferencesStore, type UserPreferencesState } from '@/store/userPreferencesStore';
import type { AppPreferencesUpdate } from '@/types/appPreferences';
import {
  parseHexColor,
  parseRgbString,
  contrastRatio,
  WCAG_AA_NORMAL,
} from '@/utils/contrastRatio';
import {
  applyThemeModeToDocument,
  getResolvedThemeMode,
  THEME_MEDIA_QUERY,
} from '@/utils/themeMode';
import { normalizeSurfacePalette, normalizeVisionMode } from '@/types/appearance';
import { getLexTheme, normalizeLexTheme } from '@/types/themeRegistry';
import { pickAccentContrast } from '@/utils/themeGenerator';
import { docketParalegalApi } from '@/features/tasks/components/DocketParalegalCard';

// Docket Paralegal standard issue (2026-10-22): once per browser session, after the signed-in user's preferences
// load, ask the server to issue the lawyer's paralegal if they never had one. Fire-and-forget; a failure here
// must never touch preferences or the sign-in.
const PARALEGAL_ENSURED_KEY = 'edgelex.docketParalegal.ensured';
function ensureDocketParalegalOnce(): void {
  try {
    if (sessionStorage.getItem(PARALEGAL_ENSURED_KEY) === '1') return;
    sessionStorage.setItem(PARALEGAL_ENSURED_KEY, '1');
  } catch {
    // Storage unavailable: still try once for this load.
  }
  void docketParalegalApi.ensure().catch(() => undefined);
}

/** Enable smooth color transitions briefly during theme/accent switches, then remove */
const enableThemeTransition = () => {
  const root = document.documentElement;
  root.classList.add('lex-transitioning');
  // Remove after transitions complete (match --lex-transition-medium 220ms + buffer)
  setTimeout(() => root.classList.remove('lex-transitioning'), 350);
};

const resolveCssColor = (cssColor: string): string | null => {
  if (typeof document === 'undefined' || !document.body) return null;

  const probe = document.createElement('span');
  probe.style.position = 'fixed';
  probe.style.width = '0';
  probe.style.height = '0';
  probe.style.overflow = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.backgroundColor = cssColor;
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).backgroundColor;
  probe.remove();

  return resolved || null;
};

/**
 * Apply (or clear) a single user font-color override on :root.
 * Sets the CSS variable + tagging attribute when the value passes WCAG AA OR
 * the force flag is true. Removes both when the value is null/invalid/failing.
 * Used by useApplyAppearancePreferences to drive heading + body pickers
 * symmetrically with independent contrast gates.
 */
const applyFontColorOverride = (
  root: HTMLElement,
  cssVar: string,
  attr: string,
  color: string | null | undefined,
  force: boolean | undefined,
  bgRgb: ReturnType<typeof parseRgbString>,
): void => {
  const fgRgb = color ? parseHexColor(color) : null;
  if (fgRgb && bgRgb) {
    const ratio = contrastRatio(fgRgb, bgRgb);
    const passes = ratio >= WCAG_AA_NORMAL;
    if (passes || force) {
      root.style.setProperty(cssVar, color!);
      root.setAttribute(attr, 'user');
      return;
    }
  }
  root.style.removeProperty(cssVar);
  root.removeAttribute(attr);
};

export function useApplyAppearancePreferences(): void {
  const appearance = useUserPreferencesStore((state) => state.appearancePreferences);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    // Enable smooth transition on theme switch (skip first render to avoid flash)
    if (!isFirstRender.current) enableThemeTransition();
    const resolved = getResolvedThemeMode(appearance.themeMode);
    applyThemeModeToDocument(resolved);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('edgelex-theme-change', { detail: appearance.themeMode }));
    }

    if (appearance.themeMode === 'system') {
      const media = window.matchMedia(THEME_MEDIA_QUERY);
      const handler = (event: MediaQueryListEvent | MediaQueryList) => {
        applyThemeModeToDocument(event.matches ? 'dark' : 'light');
      };
      handler(media);
      media.addEventListener('change', handler as EventListener);
      return () => media.removeEventListener('change', handler as EventListener);
    }

    return undefined;
  }, [appearance.themeMode]);

  useEffect(() => {
    const root = document.documentElement;
    // Enable smooth transition on accent/neutral/density change (skip first render)
    if (!isFirstRender.current) enableThemeTransition();
    applyThemeModeToDocument(getResolvedThemeMode(appearance.themeMode));
    root.setAttribute('data-accent', appearance.accentColor);
    root.setAttribute('data-neutral', normalizeSurfacePalette(appearance.neutralBase));
    root.setAttribute('data-density', appearance.density);
    root.setAttribute('data-contrast', appearance.contrastMode);

    const customAccent = appearance.accentCustomHex || '#3b82f6';
    root.style.setProperty('--lex-accent-custom', customAccent);

    // Lex theme preset + vision-assistive mode. The attribute drives the
    // frame-tint rules in lex-theme.css; the tint strengths come from the
    // registry (0% for 'custom'/default — exact current look).
    const lexTheme = normalizeLexTheme(appearance.lexTheme);
    const activeTheme = getLexTheme(lexTheme);
    root.setAttribute('data-lex-theme', lexTheme);
    root.setAttribute('data-vision', normalizeVisionMode(appearance.visionMode));
    root.style.setProperty('--lex-frame-tint-light', `${activeTheme?.frameTint.light ?? 0}%`);
    root.style.setProperty('--lex-frame-tint-dark', `${activeTheme?.frameTint.dark ?? 0}%`);

    // Readable text on a custom accent: the CSS palette hardcodes white for
    // [data-accent="custom"], which fails on light seeds (yellows). Pick
    // white/ink per WCAG and expose it as --lex-accent-custom-contrast.
    if (appearance.accentColor === 'custom') {
      root.style.setProperty('--lex-accent-custom-contrast', pickAccentContrast(customAccent));
    } else {
      root.style.removeProperty('--lex-accent-custom-contrast');
    }

    const scale = Number.isFinite(appearance.fontScale) ? appearance.fontScale : 1;
    root.style.setProperty('--lex-font-scale', `${scale}`);
    // Round to whole px — fractional root sizes soften text rendering
    root.style.fontSize = `${Math.round(16 * scale)}px`;

    // Apply per-category font-color overrides. Each picker (heading + body)
    // is independently validated against the target app surface and either
    // applied or skipped. When applied, the corresponding CSS variable is set
    // on :root and a data-text-override-* attribute is tagged so the CSS
    // derivation rules in lex-theme.css can fan out tertiary/muted from the
    // appropriate source. Failing values stay in storage but are not applied;
    // the app falls back to theme defaults. The modal escape-zone CSS is the
    // other half of the lockout-prevention contract.
    //
    // No-regressions: when only fontCustomColor (heading) is set and
    // fontBodyColor is null, the CSS rule
    //   [data-text-override-heading="user"]:not([data-text-override-body="user"])
    // derives secondary/tertiary/muted from primary — preserving v1 behavior.
    // Resolve the target surface through a fresh, non-transitioning element.
    // Reading body.backgroundColor during a light/dark transition can return
    // the old or interpolated color, leaving an invalid text override applied
    // until refresh.
    const resolvedSurfaceBg = resolveCssColor('var(--lex-surface-app)');
    const bgRgb = parseRgbString(resolvedSurfaceBg || getComputedStyle(document.body).backgroundColor);
    applyFontColorOverride(
      root,
      '--lex-text-primary',
      'data-text-override-heading',
      appearance.fontCustomColor,
      appearance.fontCustomColorForce,
      bgRgb,
    );
    applyFontColorOverride(
      root,
      '--lex-text-secondary',
      'data-text-override-body',
      appearance.fontBodyColor,
      appearance.fontBodyColorForce,
      bgRgb,
    );

    // Remove loading guard after first paint
    requestAnimationFrame(() => {
      root.classList.remove('lex-loading');
      isFirstRender.current = false;
    });
  }, [appearance.accentColor, appearance.accentCustomHex, appearance.neutralBase, appearance.density, appearance.contrastMode, appearance.fontScale, appearance.fontCustomColor, appearance.fontCustomColorForce, appearance.fontBodyColor, appearance.fontBodyColorForce, appearance.themeMode, appearance.lexTheme, appearance.visionMode]);
}

type PreferencesSnapshot = Pick<
  UserPreferencesState,
  'appearancePreferences' | 'notificationPreferences' | 'appPreferences'
>;

interface PreferencesChanges {
  appearancePreferences?: Partial<UserPreferencesState['appearancePreferences']>;
  notificationPreferences?: Partial<UserPreferencesState['notificationPreferences']>;
  appPreferences?: AppPreferencesUpdate;
}

/** Retry schedule for the server load; saves stay off until it succeeds. */
const LOAD_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];

const snapshotOf = (state: PreferencesSnapshot): PreferencesSnapshot => ({
  appearancePreferences: state.appearancePreferences,
  notificationPreferences: state.notificationPreferences,
  appPreferences: state.appPreferences,
});

const sameValue = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a) === JSON.stringify(b);

const changedFields = (base: object, next: object): Record<string, unknown> => {
  const baseRecord = base as Record<string, unknown>;
  const changes: Record<string, unknown> = {};
  Object.entries(next).forEach(([key, value]) => {
    if (!sameValue(baseRecord[key], value)) changes[key] = value;
  });
  return changes;
};

/**
 * Field-level difference between two preference snapshots, shaped as a
 * partial update. The backend merges partial updates, so a save carries only
 * what the user changed and can never reset fields it did not touch.
 */
const diffPreferences = (
  base: PreferencesSnapshot,
  next: PreferencesSnapshot,
): PreferencesChanges | null => {
  const changes: PreferencesChanges = {};

  const appearance = changedFields(base.appearancePreferences, next.appearancePreferences);
  if (Object.keys(appearance).length > 0) {
    changes.appearancePreferences = appearance as PreferencesChanges['appearancePreferences'];
  }

  const notifications = changedFields(base.notificationPreferences, next.notificationPreferences);
  if (Object.keys(notifications).length > 0) {
    changes.notificationPreferences = notifications as PreferencesChanges['notificationPreferences'];
  }

  const app: Record<string, unknown> = {};
  Object.entries(next.appPreferences).forEach(([section, value]) => {
    const baseSection = (base.appPreferences as unknown as Record<string, object | undefined>)[section] ?? {};
    const sectionChanges = changedFields(baseSection, value as object);
    if (Object.keys(sectionChanges).length > 0) app[section] = sectionChanges;
  });
  if (Object.keys(app).length > 0) {
    changes.appPreferences = app as AppPreferencesUpdate;
  }

  return Object.keys(changes).length > 0 ? changes : null;
};

/**
 * Keeps the preferences store and the server row in step.
 *
 * Logout clears localStorage, so right after a login the store holds only
 * placeholder defaults and the server row is the sole copy of the user's
 * choices. Saving is therefore off until the server row has loaded; the
 * loaded state becomes the baseline, and every save sends only the fields
 * that differ from it. Edits made while the load is in flight are replayed
 * on top of the server state rather than lost.
 */
export function useUserPreferencesSync(): void {
  const {
    appearancePreferences,
    notificationPreferences,
    appPreferences,
    setAllPreferences,
    markHydrated,
  } = useUserPreferencesStore();

  const [serverLoaded, setServerLoaded] = useState(false);
  const baseline = useRef<PreferencesSnapshot | null>(null);
  const loadRef = useRef<(() => void) | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleHandle = useRef<number | null>(null);
  const pendingSave = useRef<boolean>(false);

  const persist = useCallback((changes: PreferencesChanges, snapshot: PreferencesSnapshot) => {
    if (!navigator.onLine) {
      pendingSave.current = true;
      return;
    }

    updateUserPreferences(changes)
      .then(() => {
        baseline.current = snapshot;
        pendingSave.current = false;
      })
      .catch(() => {
        pendingSave.current = true;
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loading = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // What the store held before the server answered (localStorage values,
    // or placeholder defaults after a login). Changes on top of it while the
    // load is in flight are the user's own edits.
    const mountSnapshot = snapshotOf(useUserPreferencesStore.getState());

    const load = async () => {
      if (loading || cancelled) return;
      loading = true;
      retryTimer = null;
      let prefs: Awaited<ReturnType<typeof fetchUserPreferences>> = null;
      try {
        prefs = await fetchUserPreferences();
      } catch {
        prefs = null;
      }
      loading = false;
      if (cancelled) return;

      if (!prefs) {
        // Never fall back to saving local values over the server row.
        if (attempt < LOAD_RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => void load(), LOAD_RETRY_DELAYS_MS[attempt]);
          attempt += 1;
        }
        return;
      }

      const store = useUserPreferencesStore.getState();
      const localEdits = diffPreferences(mountSnapshot, snapshotOf(store));
      setAllPreferences(prefs);
      baseline.current = snapshotOf(useUserPreferencesStore.getState());
      if (localEdits?.appearancePreferences) {
        store.setAppearancePreferences(localEdits.appearancePreferences);
      }
      if (localEdits?.notificationPreferences) {
        store.setNotificationPreferences(localEdits.notificationPreferences);
      }
      if (localEdits?.appPreferences) {
        store.setAppPreferences(localEdits.appPreferences);
      }
      setServerLoaded(true);
      ensureDocketParalegalOnce();
    };

    // Signal ready immediately so consumers can read the persisted values
    // while the server load runs; saving waits for serverLoaded.
    markHydrated();
    loadRef.current = () => void load();
    void load();

    return () => {
      cancelled = true;
      loadRef.current = null;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [setAllPreferences, markHydrated]);

  useEffect(() => {
    if (!serverLoaded || !baseline.current) return;

    const snapshot: PreferencesSnapshot = {
      appearancePreferences,
      notificationPreferences,
      appPreferences,
    };
    const changes = diffPreferences(baseline.current, snapshot);
    if (!changes) return;

    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }

    const win = window as Window & {
      requestIdleCallback?: (cb: (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (idleHandle.current && win.cancelIdleCallback) {
      win.cancelIdleCallback(idleHandle.current);
    }

    saveTimer.current = setTimeout(() => {
      if (win.requestIdleCallback) {
        idleHandle.current = win.requestIdleCallback(() => persist(changes, snapshot), { timeout: 2000 });
      } else {
        persist(changes, snapshot);
      }
    }, 700);

    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
      if (idleHandle.current && win.cancelIdleCallback) {
        win.cancelIdleCallback(idleHandle.current);
      }
    };
  }, [appearancePreferences, notificationPreferences, appPreferences, serverLoaded, persist]);

  useEffect(() => {
    const handleOnline = () => {
      if (!baseline.current) {
        loadRef.current?.();
        return;
      }
      if (!pendingSave.current) return;
      const snapshot = snapshotOf(useUserPreferencesStore.getState());
      const changes = diffPreferences(baseline.current, snapshot);
      if (!changes) {
        pendingSave.current = false;
        return;
      }
      persist(changes, snapshot);
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [persist]);
}
